import { useCallback, useEffect, useState } from 'react';
import { useSearchParams, useNavigate, useParams } from 'react-router-dom';
import { api } from '@/api/weather/area/api';
import PageHeader from '@components/common/PageHeader';
import { toastWarning } from '@components/common/Toast/CustomToast';
import { Alert } from '@utils/alert';
import ActionButton from '@/components/common/ActionButton/ActionButton';
import TextInput from '@components/common/TextInput/TextInput';
import { WeatherAreaUpdateReq } from '@/api/weather/area/type';

const AreaDetail = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { areaIdx } = useParams<{ areaIdx: string }>();

    const [formData, setFormData] = useState({
        areaIdx: 0,
        regionName1: '',
        regionName2: '',
        regionName3: '',
        longCode: '',
        shortCode: '',
        nx: '',
        ny: '',
        longitude: '',
        latitude: '',
    });

    const getAreaInfo = useCallback(async () => {
        const result = await api.weatherAreaInfo(Number(areaIdx));

        setFormData({
            areaIdx: result.areaIdx || 0,
            regionName1: result.regionName1 || '',
            regionName2: result.regionName2 || '',
            regionName3: result.regionName3 || '',
            longCode: result.longCode || '',
            shortCode: result.shortCode || '',
            nx: result.nx || '',
            ny: result.ny || '',
            longitude: result.longitude || '',
            latitude: result.latitude || '',
        });
    }, [areaIdx]);

    const handleInputChange = (field: string, value: string | number) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const validateForm = async () => {
        // 모든 required input의 유효성 검사
        const requiredElements = document.querySelectorAll(
            'input[data-required="true"]',
        );
        let isValid = true;

        requiredElements.forEach(element => {
            if (element.getAttribute('data-valid') === 'false') {
                isValid = false;
            }
        });

        if (!isValid) {
            toastWarning('필수 항목을 확인해주세요.');
            return false;
        }

        return true;
    };

    const handleSave = async () => {
        const isValid = await validateForm();
        if (!isValid) return;

        await Alert.confirmAndExecute(
            '저장하시겠습니까?',
            '잘못된 정보가 없는지 주의하세요.',
            true,
            async () => {
                const updateRequest: WeatherAreaUpdateReq = {
                    areaIdx: formData.areaIdx,
                    regionName1: formData.regionName1,
                    regionName2: formData.regionName2,
                    regionName3: formData.regionName3,
                    longCode: formData.longCode || undefined,
                    shortCode: formData.shortCode,
                    nx: formData.nx,
                    ny: formData.ny,
                    longitude: formData.longitude || undefined,
                    latitude: formData.latitude || undefined,
                };

                await api.weatherAreaUpdate(updateRequest).then(async () => {
                    await getAreaInfo();
                });
            },
        );
    };

    const handleCancel = (): void => {
        const returnUrl = searchParams.get('returnUrl');

        if (returnUrl) {
            navigate(`/weather/area${decodeURIComponent(returnUrl)}`);
        } else {
            navigate('/weather/area');
        }
    };

    const handleDelete = async () => {
        // 고정 비밀번호 설정 (환경변수나 설정 파일에서 가져오는 것을 권장)
        const DELETE_PASSWORD = 'ais';

        await Alert.confirmWithPassword(
            '삭제를 진행하시겠습니까?',
            '삭제후 복구가 불가능합니다. 비밀번호를 입력해주세요.',
            DELETE_PASSWORD,
            true,
            async () => {
                await api.weatherAreaDelete(formData.areaIdx).then(async () => {
                    handleCancel();
                });
            },
        );
    };

    useEffect(() => {
        getAreaInfo();
    }, [getAreaInfo]);

    return (
        <>
            <PageHeader
                title="기상대 지역 상세정보"
                breadcrumbs={[
                    { label: '홈', href: '/' },
                    { label: '기상관리' },
                    { label: '지역코드관리' },
                    { label: '기상대 지역 상세정보', active: true },
                ]}
            />

            <div className="card custom-card mt-4">
                <div className="card-header">
                    <div className="card-title">
                        기상대 지역 정보를 입력해주세요.
                    </div>
                </div>
                <div className="card-body">
                    <div className="row mb-3">
                        <div className="col-md-2">
                            <label className="form-label mb-md-0 mt-2">
                                관리번호<span className="text-danger">*</span> :
                            </label>
                        </div>
                        <div className="col-md-10">
                            <input
                                type="text"
                                className="form-control"
                                disabled
                                value={formData.areaIdx || ''}
                            />
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-2">
                            <label className="form-label mb-md-0 mt-2">
                                시,도<span className="text-danger">*</span> :
                            </label>
                        </div>
                        <div className="col-md-10">
                            <TextInput
                                value={formData.regionName1}
                                onChange={value =>
                                    handleInputChange('regionName1', value)
                                }
                                placeholder="시,도를 입력해주세요."
                                required={true}
                                label="시,도"
                            />
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-2">
                            <label className="form-label mb-md-0 mt-2">
                                구,군<span className="text-danger">*</span> :
                            </label>
                        </div>
                        <div className="col-md-10">
                            <TextInput
                                value={formData.regionName2}
                                onChange={value =>
                                    handleInputChange('regionName2', value)
                                }
                                placeholder="구,군을 입력해주세요."
                                required={true}
                                label="구,군"
                            />
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-2">
                            <label className="form-label mb-md-0 mt-2">
                                동,읍,면<span className="text-danger">*</span> :
                            </label>
                        </div>
                        <div className="col-md-10">
                            <TextInput
                                value={formData.regionName3}
                                onChange={value =>
                                    handleInputChange('regionName3', value)
                                }
                                placeholder="동,읍,면을 입력해주세요."
                                required={true}
                                label="동,읍,면"
                            />
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-2">
                            <label className="form-label mb-md-0 mt-2">
                                중기지역코드 :
                            </label>
                        </div>
                        <div className="col-md-10">
                            <TextInput
                                value={formData.longCode}
                                onChange={value =>
                                    handleInputChange('longCode', value)
                                }
                                placeholder="중기지역코드를 입력해주세요."
                                required={false}
                            />
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-2">
                            <label className="form-label mb-md-0 mt-2">
                                단기지역코드
                                <span className="text-danger">*</span> :
                            </label>
                        </div>
                        <div className="col-md-10">
                            <TextInput
                                value={formData.shortCode}
                                onChange={value =>
                                    handleInputChange('shortCode', value)
                                }
                                placeholder="단기지역코드를 입력해주세요."
                                required={true}
                                label="단기지역코드"
                            />
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-2">
                            <label className="form-label mb-md-0 mt-2">
                                격자X<span className="text-danger">*</span> :
                            </label>
                        </div>
                        <div className="col-md-10">
                            <TextInput
                                value={formData.nx}
                                onChange={value =>
                                    handleInputChange('nx', value)
                                }
                                placeholder="격자X를 입력해주세요."
                                required={true}
                                label="격자X"
                                pattern="^\d+$"
                                errorMessage="숫자만 입력 가능합니다."
                            />
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-2">
                            <label className="form-label mb-md-0 mt-2">
                                격자Y<span className="text-danger">*</span> :
                            </label>
                        </div>
                        <div className="col-md-10">
                            <TextInput
                                value={formData.ny}
                                onChange={value =>
                                    handleInputChange('ny', value)
                                }
                                placeholder="격자Y를 입력해주세요."
                                required={true}
                                label="격자Y"
                                pattern="^\d+$"
                                errorMessage="숫자만 입력 가능합니다."
                            />
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-2">
                            <label className="form-label mb-md-0 mt-2">
                                경도 :
                            </label>
                        </div>
                        <div className="col-md-10">
                            <TextInput
                                value={formData.longitude}
                                onChange={value =>
                                    handleInputChange('longitude', value)
                                }
                                placeholder="경도를 입력해주세요."
                                required={false}
                                pattern="^-?\d+(\.\d+)?$"
                                errorMessage="올바른 경도 형식을 입력해주세요."
                            />
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-2">
                            <label className="form-label mb-md-0 mt-2">
                                위도 :
                            </label>
                        </div>
                        <div className="col-md-10">
                            <TextInput
                                value={formData.latitude}
                                onChange={value =>
                                    handleInputChange('latitude', value)
                                }
                                placeholder="위도를 입력해주세요."
                                required={false}
                                pattern="^-?\d+(\.\d+)?$"
                                errorMessage="올바른 위도 형식을 입력해주세요."
                            />
                        </div>
                    </div>
                </div>

                <div className="card-footer">
                    <div className="btn-list text-end">
                        <ActionButton type="save" onClick={handleSave} />
                        <ActionButton type="delete" onClick={handleDelete} />
                        <ActionButton type="cancel" onClick={handleCancel} />
                    </div>
                </div>
            </div>
        </>
    );
};

export default AreaDetail;
